import type { FC, SyntheticEvent } from 'react';
import { createElement, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Drawer, Form, Space } from 'antd';
import { getSizeAndClassName } from 'components/common/form-drawer/helpers/get-size-and-class-name';
import type { IFormDrawerProps } from 'components/common/form-drawer/types';
import { EFormDrawerSize } from 'components/common/form-drawer/types';
import type { ITransportFormFields } from 'components/transport/transport-form';

export const FormDrawer = <T extends FC<any> = FC<any>>(props: IFormDrawerProps<T>) => {
  const {
    formComponent,
    formProps,
    isOpen,
    formInstance,
    title,
    isLoading = false,
    size = EFormDrawerSize.DEFAULT,
    onClose,
    displayMaskClickConfirmation,
    onFinish,
    preventCloseOnFinish = false,
  } = props;

  const formId = useId();
  const { t } = useTranslation();
  const [form] = Form.useForm(formInstance);

  const closeForm = () => {
    onClose();
    form.resetFields();
  };

  const handleClose = (e: SyntheticEvent) => {
    // just to identify that click was performed on drawer mask element (.ant-drawer-mask)
    const isClickOnDrawerMaskElement = e.currentTarget.className === 'ant-drawer-mask';

    if (displayMaskClickConfirmation && isClickOnDrawerMaskElement) {
      confirm(t('common:on_close_confirmation', { title })) && closeForm();
    } else {
      closeForm();
    }
  };

  const handleFinish = async (values: ITransportFormFields) => {
    await onFinish(values);

    if (!preventCloseOnFinish) {
      closeForm();
    }
  };

  useEffect(() => {
    if (isOpen) {
      formProps?.initialValues && form.setFieldsValue(formProps.initialValues);
    } else {
      form.resetFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isOpen]);

  const { drawerSize, className } = getSizeAndClassName(size);

  return (
    <Drawer
      rootClassName={className}
      title={title}
      placement='right'
      open={isOpen}
      size={drawerSize}
      extra={
        <Space>
          <Button form={formId} htmlType='submit' type='primary' loading={isLoading}>
            {t('common:save')}
          </Button>
        </Space>
      }
      onClose={handleClose}>
      {isOpen ? createElement(formComponent, { ...formProps, form, formId, onFinish: handleFinish }) : null}
    </Drawer>
  );
};
